/**
 * Shared helpers for the reorder sheet views.
 *
 * The reorder sheet is rendered in three places — the main tab, the supplier
 * dashboard modal, and a saved snapshot — so grouping, sorting and filtering
 * live here rather than in any one of them.
 */

export const money = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const num = (n) =>
    n === null || n === undefined
        ? '—'
        : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export const UNMAPPED = 'Unmapped';

export const supplierOf = (item) => item.supplier || UNMAPPED;

/**
 * Columns of the item table, in order.
 *
 * `days_of_cover` is relabelled "Month cover" when the international filter is
 * on: international lead times run in months, so days are noise there.
 */
/**
 * Columns, in order, each with an explicit width.
 *
 * The widths are not cosmetic: the table runs `table-layout: fixed`, which the
 * virtualizer needs — with `auto` the browser sizes columns from the rows it
 * can see, so the layout would shift on every scroll as different rows mount.
 *
 * They are percentages rather than pixels so the table always fills its card
 * and shrinks on a narrow window. Pixel widths made the total 1,344px, which
 * clipped the right-hand columns as soon as the viewport was smaller than that.
 * `.sr-table` carries a min-width so it degrades to a horizontal scroll instead
 * of squeezing the numbers into nothing.
 *
 * The percentages are proportional to each column's *widest real value*, not
 * eyeballed. Measured on the live sheet, the tightest columns need:
 *
 *   Part #        100px  "65430811" plus the multi-supplier ⚠
 *   Reorder qty   101px  the number plus the ↺ reset button
 *   Reorder value  92px  "1,189,076.30"
 *   Qty out        91px  "253,080.75"
 *   Target stock   91px  "124,164.63"
 *   Unit           52px  "TRAY"
 *
 * An even split starved these and the cells ellipsised — a truncated part
 * number or order quantity is worse than useless, so BASE_MIN_WIDTH is set
 * where the widest of them still fits and the table scrolls below that.
 *
 * Labels match the standalone stock-movement tool one for one, so anyone
 * reading both sees the same words for the same number.
 *
 * `wrap: true` marks the text columns that must show their value in full — a
 * part description truncated to "SUNFLOWER OIL GOLDEN BEA…" is not something
 * you can put on a purchase order. Those cells wrap, which is why the
 * virtualizer measures rows rather than assuming a fixed height.
 */
export const COLUMNS = [
    { key: 'part_number', label: 'Part #', width: '9%' },
    { key: 'description', label: 'Description', wrap: true, width: '20%' },
    { key: 'unit', label: 'Unit', width: '4.5%' },
    { key: 'quantity_out', label: 'Qty Out', right: true, width: '7%' },
    { key: 'stock_as_on', label: 'Stock As On', right: true, width: '7%' },
    { key: 'avg_daily_usage', label: 'Avg Daily Use', right: true, width: '7.5%' },
    { key: 'reorder_point', label: 'Reorder Pt', right: true, width: '7%' },
    { key: 'target_stock', label: 'Target Stock', right: true, width: '7%' },
    { key: 'days_of_cover', label: 'Days Cover', right: true, width: '6%' },
    { key: 'needs_reorder', label: 'Reorder?', width: '7%' },
    { key: 'reorder_qty', label: 'Reorder Qty', right: true, width: '10%' },
    { key: 'reorder_value', label: 'Reorder Value', right: true, width: '8%' }
];

export const SUPPLIER_COLUMN = {
    key: 'supplier',
    label: 'Supplier',
    wrap: true,
    width: '14%'
};
export const BRANCH_COLUMN = { key: 'branch', label: 'Branch', wrap: true, width: '9%' };

/**
 * The table width the percentages above were measured against.
 *
 * Supplier and Branch are optional, so a table showing them totals more than
 * 100% and the browser scales every column down to fit — exactly the squeeze
 * the percentages were picked to avoid. Scaling the floor by the same total
 * keeps each column at the px width it was sized for.
 */
export const BASE_MIN_WIDTH = 1180;

export const tableMinWidth = (columns) =>
    Math.round(
        (BASE_MIN_WIDTH * columns.reduce((sum, col) => sum + parseFloat(col.width), 0)) / 100
    );

export const DEFAULT_SORT = { key: 'reorder_value', asc: false };

/** Nulls always sort last, whichever direction is active. */
export const sortItems = (items, { key, asc }) =>
    [...items].sort((a, b) => {
        let av = a[key];
        let bv = b[key];
        if (av === null || av === undefined) av = asc ? Infinity : -Infinity;
        if (bv === null || bv === undefined) bv = asc ? Infinity : -Infinity;
        if (typeof av === 'string' || typeof bv === 'string') {
            av = String(av).toLowerCase();
            bv = String(bv).toLowerCase();
        }
        if (av < bv) return asc ? -1 : 1;
        if (av > bv) return asc ? 1 : -1;
        return 0;
    });

export const filterItems = (items, { search, onlyNeeded, onlyMultiSupplier, onlyInternational }) => {
    const term = (search || '').trim().toLowerCase();
    return (items || []).filter((item) => {
        if (onlyNeeded && !item.needs_reorder) return false;
        if (onlyMultiSupplier && !item.multiple_suppliers) return false;
        if (onlyInternational && !item.is_international_supplier) return false;
        if (!term) return true;
        return (
            String(item.part_number || '').toLowerCase().includes(term) ||
            String(item.description || '').toLowerCase().includes(term) ||
            supplierOf(item).toLowerCase().includes(term)
        );
    });
};

/**
 * Group into supplier blocks, worst first: most items needing reorder, then
 * highest reorder value, then alphabetically. That ordering is what makes the
 * blocks a work queue rather than a directory.
 */
export const groupBySupplier = (items) => {
    const map = new Map();
    for (const item of items) {
        const key = supplierOf(item);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    }
    return [...map.entries()]
        .map(([supplier, groupItems]) => ({
            supplier,
            items: groupItems,
            needingCount: groupItems.filter((i) => i.needs_reorder).length,
            reorderValue: groupItems.reduce((sum, i) => sum + (i.reorder_value || 0), 0),
            isInternational: groupItems.some((i) => i.is_international_supplier)
        }))
        .sort((a, b) => {
            if (b.needingCount !== a.needingCount) return b.needingCount - a.needingCount;
            if (b.reorderValue !== a.reorderValue) return b.reorderValue - a.reorderValue;
            return a.supplier.localeCompare(b.supplier);
        });
};

/** Keeps the summary honest after a manual override, without a round trip. */
export const recomputeSummary = (items) => ({
    total_items: items.length,
    items_needing_reorder: items.filter((i) => i.needs_reorder).length,
    total_reorder_value:
        Math.round(items.reduce((sum, i) => sum + (i.reorder_value || 0), 0) * 100) / 100
});

export const coverValue = (item, monthCover) => {
    if (item.days_of_cover === null || item.days_of_cover === undefined) return '—';
    return monthCover ? Math.round((item.days_of_cover / 30) * 10) / 10 : item.days_of_cover;
};

/** The row's branch — items only carry one on combined sheets. */
export const branchIdOf = (item, fallback) => item.business_unit_id || fallback || '';
